import { transformToDatabase } from "./dataTransformers";
import { toISODateString } from "./dateUtils";
import { type SubscriptionFormData } from "./formDataHelpers";
import {
  validateStartDateChange,
  shouldAllowStartDateChange,
} from "./startDateValidation";
import { offlineMutate } from "@/lib/offlineToast";
import { type DatabaseSubscription } from "@/hooks/useSubscriptions";
import { type Subscription } from "@/types/subscription";

type ToastNotifier = {
  error: (title: string, data?: { description?: string }) => unknown;
};

// Type for subscription data from form (without database fields)
export type FormSubscriptionData = Omit<
  Subscription,
  | "id"
  | "createdAt"
  | "updatedAt"
  | "paymentHistory"
  | "lifecycleEvents"
  | "subscriptionPeriods"
>;

export const validateStartDateChangeSubmission = (
  formData: SubscriptionFormData,
  isEditing: boolean,
  subscription: Subscription | undefined,
  toast: ToastNotifier,
): { valid: boolean; validation?: any } => {
  // Early return for non-editing mode
  if (!isEditing) {
    return { valid: true };
  }

  // Handle editing mode with missing subscription data
  if (!subscription) {
    console.warn("Validation called in editing mode without subscription data");
    console.warn("FormData:", formData);
    console.warn("isEditing:", isEditing);
    console.warn("Subscription type:", typeof subscription);
    console.warn("Subscription value:", subscription);
    return { valid: true }; // Allow the operation to proceed
  }

  const newStartDate = new Date(formData.startDate);
  const oldStartDate = subscription.startDate;

  // Normalize to calendar date (ignore timezone/time component)
  const toDateOnly = (d: Date) => {
    if (!(d instanceof Date) || isNaN(d.getTime())) return "";
    return d.toISOString().slice(0, 10); // YYYY-MM-DD
  };
  const formDateStr =
    typeof formData.startDate === "string"
      ? formData.startDate.slice(0, 10)
      : toDateOnly(newStartDate);
  const oldDateStr = toDateOnly(oldStartDate);

  // If calendar date is unchanged, skip validation entirely
  if (formDateStr === oldDateStr) {
    return { valid: true };
  }

  // Fuzzy equality: handle 1-day timezone drift (UTC vs local date-only parsing)
  try {
    const utcOld = new Date(oldDateStr + "T00:00:00Z");
    const utcNew = new Date(formDateStr + "T00:00:00Z");
    const dayDiff = Math.abs((utcNew.getTime() - utcOld.getTime()) / 86400000);
    const hourDelta = Math.abs(
      (newStartDate.getTime() - oldStartDate.getTime()) / 3600000,
    );
    // If apparent shift is exactly 1 day but underlying timestamps are within 36h,
    // likely a timezone normalization artifact – treat as unchanged.
    if (dayDiff === 1 && hourDelta <= 36) {
      return { valid: true };
    }
  } catch (e) {
    console.warn(
      "Fuzzy date comparison failed, proceeding with strict check",
      e,
    );
  }

  // Only validate if calendar date actually changed
  {
    const validation = validateStartDateChange(subscription, newStartDate);

    // Check if change is allowed
    const permissionCheck = shouldAllowStartDateChange(subscription);

    const hasHighVolumeWarning = validation.warnings.some((w: string) =>
      w.includes("High payment volume"),
    );

    if (!permissionCheck.allowed && hasHighVolumeWarning) {
      toast.error("Start Date Change Blocked", { description: permissionCheck.reason });
      return { valid: false, validation };
    }

    // Show warnings for significant changes (could be moved to a UI component)
    if (validation.warnings.length > 0) {
      console.warn("Start date change warnings:", validation.warnings);
    }

    return { valid: true, validation };
  }

  return { valid: true };
};

export const createSubscriptionDataObject = (
  formData: SubscriptionFormData,
  categories: any[],
  tagIds: string[],
): FormSubscriptionData => {
  // Derive primary category name from first selected tag (for legacy display)
  const primaryCategoryName =
    categories.find((cat) => cat.id === (tagIds[0] || ""))?.name ||
    "Uncategorized";

  const subscriptionData: FormSubscriptionData = {
    nextPaymentSource: "manual" as const,
    category: primaryCategoryName,
    reconciliationStatus:
      formData.reconciliationStatus as Subscription["reconciliationStatus"],
    notes: formData.notes,
    sentiment: formData.sentiment,
    cancellationDate:
      formData.status === "cancelled" &&
      formData.cancellationDate &&
      formData.cancellationDate.trim() !== ""
        ? new Date(formData.cancellationDate)
        : null,
    trialEndDate: formData.trialEnd ? new Date(formData.trialEnd) : undefined,
    pauseDate:
      formData.status === "paused" &&
      formData.pauseDate &&
      formData.pauseDate.trim() !== ""
        ? new Date(formData.pauseDate)
        : undefined,
    resumeDate:
      formData.resumeDate && formData.resumeDate.trim() !== ""
        ? new Date(formData.resumeDate)
        : undefined,
    // Required fields
    name: formData.name,
    amount: isNaN(Number(formData.amount))
      ? undefined
      : Number(formData.amount),
    currency: formData.currency,
    frequency:
      formData.frequency && formData.frequency !== "none"
        ? (formData.frequency as Subscription["frequency"])
        : undefined,
    customFrequencyDays:
      formData.frequency === "custom"
        ? formData.customFrequencyDays
        : undefined,
    nextPayment:
      formData.billingType === "pay_as_you_go" &&
      (!formData.frequency || formData.frequency === "none")
        ? null
        : new Date(formData.nextPayment),
    startDate: new Date(formData.startDate),
    status: formData.status as Subscription["status"],
    autoRenew: formData.autoRenew,
    autoRenewalDate: formData.autoRenewalDate
      ? new Date(formData.autoRenewalDate)
      : undefined,
    provider: formData.provider || undefined,
    websiteUrl: formData.websiteUrl || undefined,
    paymentMethod: formData.paymentMethod || undefined,
    // Billing type from the form (recurring | pay_as_you_go)
    billingType: formData.billingType as any,
  };

  return subscriptionData;
};

export const transformSubscriptionForDatabase = (
  subscriptionData: FormSubscriptionData,
): Omit<
  DatabaseSubscription,
  "id" | "user_id" | "created_at" | "updated_at"
> => {
  // Add required arrays before transforming
  const fullSubscriptionData: Omit<
    Subscription,
    "id" | "createdAt" | "updatedAt"
  > = {
    ...subscriptionData,
    paymentHistory: [],
    lifecycleEvents: [],
    subscriptionPeriods: [],
  };
  const dbData = transformToDatabase(fullSubscriptionData);

  // Enrich with fields not yet handled by transformToDatabase
  const dbWithExtras: Omit<
    DatabaseSubscription,
    "id" | "user_id" | "created_at" | "updated_at"
  > = {
    ...dbData,
    auto_renew: !!subscriptionData.autoRenew,
    auto_renewal_date: toISODateString(subscriptionData.autoRenewalDate),
    payment_method: subscriptionData.paymentMethod || undefined,
    provider: subscriptionData.provider || undefined,
    website_url: subscriptionData.websiteUrl || undefined,
    // Billing type stored on subscriptions
    billing_type: subscriptionData.billingType || "recurring",
    // Enforce ADR-009 Option A:
    // - PAYG with no schedule => billing_cycle = NULL, next_payment_date = NULL
    // - PAYG with schedule    => keep encoded billing_cycle, preserve next_payment_date
    // - Recurring             => preserve values
    billing_cycle:
      subscriptionData.billingType === "pay_as_you_go" &&
      !subscriptionData.frequency
        ? null
        : dbData.billing_cycle,
    next_payment_date:
      subscriptionData.billingType === "pay_as_you_go" &&
      !subscriptionData.frequency
        ? null
        : (toISODateString(subscriptionData.nextPayment) ??
          dbData.next_payment_date),
    // Ensure cancellation_date is set or cleared appropriately
    cancellation_date:
      subscriptionData.status === "cancelled"
        ? (toISODateString(subscriptionData.cancellationDate) ??
          dbData.cancellation_date ??
          null)
        : null,
    // Ensure trial_end_date is set when provided
    trial_end_date:
      toISODateString(subscriptionData.trialEndDate) ?? dbData.trial_end_date,
    // Ensure pause_date is set when provided
    pause_date:
      subscriptionData.status === "paused"
        ? (toISODateString(subscriptionData.pauseDate) ??
          dbData.pause_date ??
          null)
        : null,
    // Ensure resume_date is set when provided
    resume_date:
      toISODateString(subscriptionData.resumeDate) ??
      dbData.resume_date ??
      null,
  };

  return dbWithExtras;
};

export const performSubscriptionSave = async ({
  dbWithExtras,
  isEditing,
  subscription,
  replaceTags,
  updateSubscription,
  createSubscription,
  tagIds,
}: {
  dbWithExtras: Omit<
    DatabaseSubscription,
    "id" | "user_id" | "created_at" | "updated_at"
  >;
  isEditing: boolean;
  subscription?: Subscription;
  replaceTags: any;
  updateSubscription: any;
  createSubscription: any;
  tagIds: string[];
}) => {
  // Validate mutation objects
  if (
    !createSubscription ||
    typeof createSubscription.mutateAsync !== "function"
  ) {
    throw new Error("createSubscription mutation not properly initialized");
  }
  if (
    !updateSubscription ||
    typeof updateSubscription.mutateAsync !== "function"
  ) {
    throw new Error("updateSubscription mutation not properly initialized");
  }
  if (!replaceTags || typeof replaceTags.mutateAsync !== "function") {
    throw new Error("replaceTags mutation not properly initialized");
  }

  let result;
  if (isEditing && subscription) {
    const updateData: Partial<DatabaseSubscription> = {
      ...dbWithExtras,
    };
    result = await offlineMutate(updateSubscription, {
      id: subscription.id,
      ...updateData,
    });
    // Tags are persisted immediately on change in edit mode
  } else {
    const created = await offlineMutate<DatabaseSubscription, typeof dbWithExtras>(
      createSubscription,
      dbWithExtras,
    );
    if (created) {
      await offlineMutate(replaceTags, {
        subscriptionId: created.id,
        categoryIds: tagIds,
      });
    }
    result = created;
  }

  return result;
};

export const resetFormDataAfterSave = (
  setFormData: React.Dispatch<React.SetStateAction<SubscriptionFormData>>,
  setTagIds: React.Dispatch<React.SetStateAction<string[]>>,
  resetFormData: () => SubscriptionFormData,
) => {
  setFormData(resetFormData());
  setTagIds([]);
};
